import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import {
  FiStar, FiSearch, FiFilter, FiTrash2, FiEye,
  FiTrendingUp, FiTrendingDown, FiRefreshCw,
  FiUser, FiTruck, FiMessageSquare, FiAlertCircle, FiX
} from 'react-icons/fi';

function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterRating, setFilterRating] = useState('all');
  const [selectedReview, setSelectedReview] = useState(null);

  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getAllReviews();
      if (response && response.success && Array.isArray(response.data)) {
        setReviews(response.data);
      } else {
        setReviews([]);
        setError(response?.message || 'Réponse invalide du serveur');
      }
    } catch (err) {
      console.error('Erreur chargement avis:', err);
      setReviews([]);
      setError(err.message || 'Impossible de charger les avis');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet avis ?')) return;
    try {
      await api.deleteReview(id);
      setSelectedReview((prev) => (prev && prev.id === id ? null : prev));
      await loadReviews();
    } catch (error) {
      console.error('Erreur:', error);
      window.alert(error.message || 'Suppression impossible');
    }
  };

  const filteredReviews = reviews.filter(review => {
    const comment = (review.comment || '').toLowerCase();
    const matchesSearch =
      (review.clientName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (review.moverName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      comment.includes(searchTerm.toLowerCase());
    const matchesRating = filterRating === 'all' || parseInt(filterRating, 10) === review.rating;
    return matchesSearch && matchesRating;
  });

  const stats = {
    total: reviews.length,
    average:
      reviews.length > 0
        ? (reviews.reduce((sum, r) => sum + r.rating, 0) / reviews.length).toFixed(1)
        : '0',
    fiveStar: reviews.filter(r => r.rating === 5).length,
    oneStar: reviews.filter(r => r.rating === 1).length
  };

  const clientInitials = (name) => {
    const parts = String(name || '')
      .trim()
      .split(/\s+/)
      .filter(Boolean);
    if (parts.length === 0) return '?';
    return parts
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => (
      <FiStar
        key={i}
        className={`w-5 h-5 ${
          i < rating 
            ? 'text-yellow-400 fill-yellow-400' 
            : 'text-gray-600'
        }`}
      />
    ));
  };

  return (
    <div className="space-y-6 p-6 bg-gray-900 min-h-screen text-gray-100">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center pb-4 border-b border-yellow-600/30">
        <div>
          <h1 className="text-4xl font-extrabold text-yellow-400 mb-2 flex items-center">
            <FiStar className="w-8 h-8 mr-3" />
            Gestion des Avis
          </h1>
          <p className="text-gray-400">Consultez et gérez les avis des clients</p>
        </div>
        <button
          onClick={loadReviews}
          className="mt-4 sm:mt-0 px-5 py-2.5 bg-yellow-600 text-gray-900 rounded-lg hover:bg-yellow-500 transition-colors font-semibold flex items-center"
        >
          <FiRefreshCw className="mr-2" />
          Actualiser
        </button>
      </div>

      {error && (
        <div className="rounded-xl border border-red-500/40 bg-red-900/20 px-4 py-3 text-red-200 text-sm">
          {error}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-gray-400 uppercase">Total avis</p>
            <FiStar className="w-6 h-6 text-yellow-400" />
          </div>
          <p className="text-4xl font-bold text-yellow-400">{stats.total}</p>
        </div>
        <div className="bg-gradient-to-br from-green-600/10 to-emerald-600/10 border border-green-600/30 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-gray-400 uppercase">Note moyenne</p>
            <FiTrendingUp className="w-6 h-6 text-green-400" />
          </div>
          <div className="flex items-center">
            <p className="text-4xl font-bold text-green-400">{stats.average}</p>
            <div className="ml-2 flex">
              {renderStars(Math.round(parseFloat(stats.average)))}
            </div>
          </div>
        </div>
        <div className="bg-gradient-to-br from-yellow-600/10 to-orange-600/10 border border-yellow-600/30 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-gray-400 uppercase">5 étoiles</p>
            <FiStar className="w-6 h-6 text-yellow-400" />
          </div>
          <p className="text-4xl font-bold text-yellow-400">{stats.fiveStar}</p>
        </div>
        <div className="bg-gradient-to-br from-red-600/10 to-rose-600/10 border border-red-600/30 rounded-xl p-6">
          <div className="flex items-center justify-between mb-4">
            <p className="text-sm font-semibold text-gray-400 uppercase">1 étoile</p>
            <FiAlertCircle className="w-6 h-6 text-red-400" />
          </div>
          <p className="text-4xl font-bold text-red-400">{stats.oneStar}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par client, déménageur ou commentaire..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500"
            />
          </div>
          <select
            value={filterRating}
            onChange={(e) => setFilterRating(e.target.value)}
            className="px-4 py-3 bg-gray-900/50 border border-gray-700 text-white rounded-lg focus:ring-2 focus:ring-yellow-500"
          >
            <option value="all">Toutes les notes</option>
            <option value="5">5 étoiles</option>
            <option value="4">4 étoiles</option>
            <option value="3">3 étoiles</option>
            <option value="2">2 étoiles</option>
            <option value="1">1 étoile</option>
          </select>
        </div>
      </div>

      {/* Reviews List */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-yellow-500"></div>
          </div>
        ) : filteredReviews.length === 0 ? (
          <div className="text-center py-20 bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl px-6">
            <FiStar className="w-20 h-20 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-2">Aucun avis trouvé</p>
            <p className="text-gray-500 text-sm max-w-xl mx-auto">
              Cette page liste uniquement les avis enregistrés en base (<span className="text-gray-400">demenageur_evaluations</span>),
              après une mission terminée et une évaluation côté client. Les étoiles sur les cartes déménageur peuvent aussi venir du
              profil (<span className="text-gray-400">mover_profiles</span>) ou de l&apos;aperçu démo, sans ligne d&apos;avis ici.
              Vérifiez que le backend utilisé par cette page est le même que l&apos;app : variable{' '}
              <span className="text-yellow-600/90">REACT_APP_API_URL</span> dans <span className="text-gray-400">web-frontend/.env</span>
              (puis redémarrer <span className="text-gray-400">npm start</span>).
            </p>
          </div>
        ) : (
          filteredReviews.map((review) => (
            <div
              key={review.id}
              className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-6 hover:border-yellow-500/50 transition-all"
            >
              <div className="flex justify-between items-start mb-4">
                <div className="flex-1">
                  <div className="flex items-center space-x-4 mb-3">
                    <div className="flex items-center">
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                        {clientInitials(review.clientName)}
                      </div>
                      <div className="ml-3">
                        <p className="text-white font-semibold">{review.clientName}</p>
                        <p className="text-sm text-gray-400">
                          Déménageur: {review.moverName}
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2 mb-2">
                    {renderStars(review.rating)}
                    <span className="text-gray-400 text-sm ml-2">
                      {review.createdAt
                        ? new Date(review.createdAt).toLocaleDateString('fr-FR')
                        : '—'}
                    </span>
                    {review.isVerified && (
                      <span className="px-2 py-1 text-xs bg-green-500/20 text-green-400 rounded border border-green-500/30">
                        Vérifié
                      </span>
                    )}
                  </div>
                  <p className="text-gray-300 mb-3">
                    {review.comment ? review.comment : <span className="italic text-gray-500">Aucun commentaire</span>}
                  </p>
                  <div className="flex items-center space-x-4 text-sm text-gray-400">
                    <div className="flex items-center">
                      <FiTruck className="w-4 h-4 mr-1" />
                      {review.moveFrom} → {review.moveTo}
                    </div>
                    <div className="flex items-center">
                      <FiMessageSquare className="w-4 h-4 mr-1" />
                      Déménagement #
                      {review.moveId ? String(review.moveId).substring(0, 8) : '—'}
                    </div>
                  </div>
                </div>
                <div className="flex space-x-2 ml-4">
                  <button
                    onClick={() => setSelectedReview(review)}
                    className="text-blue-400 hover:text-blue-300 p-2 hover:bg-blue-400/10 rounded"
                  >
                    <FiEye className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(review.id)}
                    className="text-red-400 hover:text-red-300 p-2 hover:bg-red-400/10 rounded"
                  >
                    <FiTrash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Detail Modal */}
      {selectedReview && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 border border-yellow-600/30 rounded-xl p-8 max-w-2xl w-full">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-3xl font-bold text-white">Détails de l'avis</h2>
              <button
                type="button"
                onClick={() => setSelectedReview(null)}
                className="text-gray-400 hover:text-white"
                aria-label="Fermer"
              >
                <FiX className="w-6 h-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <p className="text-gray-400 mb-1">Client</p>
                <p className="text-white font-semibold">{selectedReview.clientName}</p>
              </div>
              <div>
                <p className="text-gray-400 mb-1">Déménageur</p>
                <p className="text-white font-semibold">{selectedReview.moverName}</p>
              </div>
              <div>
                <p className="text-gray-400 mb-2">Note</p>
                <div className="flex items-center">
                  {renderStars(selectedReview.rating)}
                  <span className="ml-2 text-white font-semibold">{selectedReview.rating}/5</span>
                </div>
              </div>
              <div>
                <p className="text-gray-400 mb-1">Commentaire</p>
                <p className="text-white">
                  {selectedReview.comment || <span className="italic text-gray-500">Aucun commentaire</span>}
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-gray-400 mb-1">Trajet</p>
                  <p className="text-white">{selectedReview.moveFrom} → {selectedReview.moveTo}</p>
                </div>
                <div>
                  <p className="text-gray-400 mb-1">Date du déménagement</p>
                  <p className="text-white">
                    {selectedReview.moveDate
                      ? new Date(selectedReview.moveDate).toLocaleDateString('fr-FR')
                      : '—'}
                  </p>
                </div>
              </div>
            </div>
            <button
              onClick={() => setSelectedReview(null)}
              className="mt-6 w-full px-6 py-3 bg-yellow-600 hover:bg-yellow-500 text-gray-900 font-semibold rounded-lg transition-colors"
            >
              Fermer
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminReviews;

